/**
 * report-switch.tsx — Bật/tắt "tính vào báo cáo" cho một nick.
 *
 * Vì sao cần: gỡ một nick không được phép làm số liệu quá khứ tụt xuống —
 * tháng trước nick đó bán hàng thật. Nhưng nick mô phỏng AI và nick cá nhân
 * thì không bao giờ được lẫn vào doanh số. Hai việc đó không suy ra được từ
 * trạng thái kết nối, nên phải để người vận hành đánh dấu.
 *
 * Chỉ quản trị đổi được; người khác vẫn thấy nhãn.
 */
import { toast } from 'sonner'
import { BarChart3, EyeOff, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import { useSetCountInReports, type ChannelAccount } from '@/hooks/use-integrations'

export function ReportSwitch({ acc, canEdit }: { acc: ChannelAccount; canEdit: boolean }) {
  const dat = useSetCountInReports()
  const tinh = acc.countInReports !== false

  const nhan = tinh ? 'Tính vào báo cáo' : 'Không tính báo cáo'
  const lop = tinh
    ? 'border-border bg-muted text-muted-foreground'
    : 'border-warning/40 bg-warning/10 text-warning'

  if (!canEdit) {
    // Nick vẫn tính vào báo cáo là chuyện bình thường — không cần nhãn.
    if (tinh) return null
    return (
      <span className={cn('inline-flex h-[18px] items-center gap-0.5 rounded-full border px-1.5 text-[10px] font-semibold', lop)}>
        <EyeOff className="h-3 w-3" />
        {nhan}
      </span>
    )
  }

  return (
    <button
      type="button"
      disabled={dat.isPending}
      title={
        tinh
          ? 'Đang tính vào tin nhắn, đơn hàng, doanh số. Bấm để loại khỏi báo cáo (nick thử nghiệm, nick cá nhân).'
          : 'Đang bị loại khỏi mọi số liệu báo cáo. Bấm để tính lại.'
      }
      onClick={() =>
        dat.mutate(
          { id: acc.id, countInReports: !tinh },
          {
            onSuccess: () =>
              toast.success(
                tinh
                  ? 'Đã loại nick này khỏi số liệu báo cáo'
                  : 'Đã tính nick này vào số liệu báo cáo',
              ),
            onError: (e) => toast.error(apiError(e)),
          },
        )
      }
      className={cn(
        'inline-flex h-[18px] shrink-0 items-center gap-0.5 rounded-full border px-1.5 text-[10px] font-semibold transition-colors hover:bg-accent',
        lop,
      )}
    >
      {dat.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : tinh ? <BarChart3 className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
      {nhan}
    </button>
  )
}
